import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import Tippy from '@tippyjs/react';
import { get, isEqual, omit, size, trim } from 'lodash';

import ErrorMessages from 'components/ErrorMessages';
import ShowDebug from 'components/ShowDebug';

import { accessApi } from 'utils/injectApi';
import { arredonda, divideArredonda, isDebug, soma } from 'utils/tools';

import PlanilhaIndicesConstrutivosLinha from './PlanilhaIndicesConstrutivosLinha';
import ResumoIndicesConstrutivosAP from './ResumoIndicesConstrutivos';

export const metadata = [
	{
		label: 'Solo criado adensável (m²) ',
		observacao: 'Valor total de pequeno, médio e grande adensamento',
		property: 'soloCriadoAdensavel',
		decimals: 2
	},
	{
		label: 'Solo criado não adensável (m²) ',
		observacao: 'Valor necessário a ser adquirido',
		property: 'soloCriadoNaoAdensavel',
		decimals: 2
	},
	{ label: 'Potencial construtivo oriundo de TPC local (m²) ', property: 'potencialConstrutivoTpcLocal', decimals: 4 },
	{
		label: 'Potencial construtivo oriundo de TPC externa (m²) ',
		property: 'potencialConstrutivoTpcRealizada',
		decimals: 4
	},
	{
		label: 'Potencial construtivo c/ origem no Leilão SMF (m²) ',
		property: 'potencialConstrutivoTpcLeilaoSmf',
		decimals: 4
	},
	{
		label: 'Potencial construtivo de imóvel inventariado (Lei nº 12.585/2019) (m²) ',
		property: 'potencialConstrutivoImovelInventariado',
		decimals: 4
	}
];

export const NOVA_AREA_UDRI = { informada: 0.0, obs: null };
export const PLANILHA_PROPERTY = 'planilhaIndicesConstrutivos';

let i = 0;
const debugLog = (...args) => isDebug && console.debug('(PIC)', ++i, ...args);
function PlanilhaIndicesConstrutivos({
	planilha,
	planilhaAnterior,
	planoDiretor,
	possuiAreaAtingida,
	possuiImovelInventariado,
	areaAtingidaMatricula,
	areaAtingidaMenorPoligonal,
	totalIaPermitido,
	totalIaMaxTotalPermitido,
	onChangeHandler,
	readOnly,
	readOnlyVersao = null,
	errors: errorsProps,
	propertyName = PLANILHA_PROPERTY,
	usuarioInterno = false
}) {
	const [isFocoModeHistory, setIsFocoModeHistory] = useState(false);
	const [hasChanges, setHasChanges] = useState(false);
	const [localErrors, setLocalErrors] = useState({});
	const primeiraVez = useRef(true);
	const [estoqueQtr, setEstoqueQtr] = useState(
		usuarioInterno
			? typeof planilha?.estoquePublicado === 'number' &&
			  typeof planilha?.estoqueCalculado === 'number' &&
			  typeof planilha?.ueuMaximo === 'number'
				? {
						estoquePublicado: planilha?.estoquePublicado,
						estoqueCalculado: planilha?.estoqueCalculado,
						ueuMaximo: planilha?.ueuMaximo
				  }
				: null
			: null
	);

	useMemo(() => {
		if (readOnlyVersao && readOnlyVersao > 1) {
			setHasChanges(!isEqual(planilhaAnterior, planilha));
		}
	}, [planilha, planilhaAnterior, readOnlyVersao]);

	const carregarEstoqueQuarteirao = useCallback(async params => {
		const response = await accessApi('EXPEDIENTES_API_URL/obtem-saldo-quarteirao', true, {
			method: 'post',
			data: params
		});
		return response.data;
	}, []);

	useEffect(() => {
		if (estoqueQtr === null && !usuarioInterno) {
			if (planoDiretor.macrozona && planoDiretor.ueu && planoDiretor.qtr) {
				const mz = planoDiretor.macrozona;
				const ueu = planoDiretor.ueu;
				const qtr = planoDiretor.qtr;
				// Mock estoqueQtr with estoqueCalculado and estoquePublicado as 100
				// setEstoqueQtr({
				// 	ueuMaximo: 100,
				// 	estoquePublicado: 100,
				// 	estoqueCalculado: 100
				// });
				// Se quiser manter o carregamento real, comente a linha acima e descomente abaixo:
				carregarEstoqueQuarteirao({ mz, ueu, qtr }).then(setEstoqueQtr);
			}
		}
	}, [planoDiretor, carregarEstoqueQuarteirao, estoqueQtr, usuarioInterno]);

	const entrarModoFoco = () => {
		if (planilhaAnterior && !isFocoModeHistory) {
			setIsFocoModeHistory(true);
		}
		setIsFocoModeHistory(true);
	};
	const sairModoFoco = () => {
		if (isFocoModeHistory) {
			setIsFocoModeHistory(false);
		}
	};

	const updatePlanilha = useCallback(
		(novaPlanilha, force) => {
			const { totalAdensavel, totalNaoAdensavel } = obtainTotalAreaCalculada(novaPlanilha);
			let changed = false;

			if (novaPlanilha.totalAreaAdensavelCalculada !== totalAdensavel) {
				novaPlanilha.totalAreaAdensavelCalculada = totalAdensavel;
				changed = true;
			}
			if (novaPlanilha.totalAreaNaoAdensavelCalculada !== totalNaoAdensavel) {
				novaPlanilha.totalAreaNaoAdensavelCalculada = totalNaoAdensavel;
				changed = true;
			}
			if (novaPlanilha.totalIaPermitido !== totalIaPermitido) {
				novaPlanilha.totalIaPermitido = totalIaPermitido;
				changed = true;
			}
			if (novaPlanilha.totalIaPermitidaMaisAdensavel !== totalIaPermitido + totalAdensavel) {
				novaPlanilha.totalIaPermitidaMaisAdensavel = totalIaPermitido + totalAdensavel;
				changed = true;
			}
			const metadeIaPermitido = parseFloat(divideArredonda(totalIaPermitido, 2, { decimais: 4 }));
			if (novaPlanilha.totalIaPermitidaMaisNaoAdensavel !== metadeIaPermitido + totalNaoAdensavel) {
				novaPlanilha.totalIaPermitidaMaisNaoAdensavel = metadeIaPermitido + totalNaoAdensavel;
				changed = true;
			}
			if (novaPlanilha.totalIaMaxTotalPermitido !== totalIaMaxTotalPermitido) {
				novaPlanilha.totalIaMaxTotalPermitido = totalIaMaxTotalPermitido;
				changed = true;
			}

			if (novaPlanilha.areaAtingidaMatricula !== areaAtingidaMatricula) {
				novaPlanilha.areaAtingidaMatricula = areaAtingidaMatricula;
				changed = true;
			}
			if (novaPlanilha.areaAtingidaMenorPoligonal !== areaAtingidaMenorPoligonal) {
				novaPlanilha.areaAtingidaMenorPoligonal = areaAtingidaMenorPoligonal;
				changed = true;
			}
			if (novaPlanilha.possuiAreaAtingida !== possuiAreaAtingida) {
				novaPlanilha.possuiAreaAtingida = possuiAreaAtingida;
				changed = true;
			}
			if (
				typeof estoqueQtr?.estoqueCalculado === 'number' &&
				novaPlanilha.estoqueCalculado !== estoqueQtr.estoqueCalculado
			) {
				novaPlanilha.estoqueCalculado = estoqueQtr?.estoqueCalculado || 0;
				changed = true;
			}
			if (
				typeof estoqueQtr?.estoquePublicado === 'number' &&
				novaPlanilha.estoquePublicado !== estoqueQtr.estoquePublicado
			) {
				novaPlanilha.estoquePublicado = estoqueQtr?.estoquePublicado || 0;
				changed = true;
			}
			if (typeof estoqueQtr?.ueuMaximo === 'number' && novaPlanilha.ueuMaximo !== estoqueQtr.ueuMaximo) {
				novaPlanilha.ueuMaximo = estoqueQtr?.ueuMaximo || 0;
				changed = true;
			}

			if (changed || force) {
				const [errorsAux, warningsAux] = validaPlanilhaIndicesConstrutivos(novaPlanilha, planoDiretor.dadosSubunidades);
				if (size(warningsAux) > 0) {
					for (const prop in warningsAux) {
						for (const message of warningsAux[prop]) {
							const warningAux = { type: 'warning', message };
							if (errorsAux[prop]) {
								errorsAux[prop].push(warningAux);
							} else {
								errorsAux[prop] = [warningAux];
							}
						}
					}
				}
				setLocalErrors(errorsAux);
				onChangeHandler(novaPlanilha, propertyName);
			}
		},
		[
			areaAtingidaMatricula,
			areaAtingidaMenorPoligonal,
			estoqueQtr,
			onChangeHandler,
			planoDiretor.dadosSubunidades,
			possuiAreaAtingida,
			propertyName,
			totalIaMaxTotalPermitido,
			totalIaPermitido
		]
	);

	const handleChange = (value, name) => {
		let novaPlanilha = { ...planilha, [name]: value };
		updatePlanilha(novaPlanilha, true);
	};

	const readOnlyAreas = useMemo(() => {
		if (!planoDiretor.dadosSubunidades) {
			return null;
		}

		/* --------------------------------- regras --------------------------------- */
		// ex: Paula Soares, 1000 - subunidade 04 -> código 01
		// Removido na história #62525 - Item 5
		const codigo01ou03 = false;
		// const codigo01ou03 = planoDiretor.dadosSubunidades.reduce(
		// 	(acc, s) => acc && ['01', '03'].includes(s?.indiceAproveitamentoMaximoAnexo?.codigo),
		// 	true
		// );

		// ex: Estr Extrema, 3384 - subunidade 01 -> código 31
		// const codigo31ou33ou35ou37 = planoDiretor.dadosSubunidades.reduce(
		// 	(acc, s) => acc && ['31', '33', '35', '37'].includes(s?.indiceAproveitamentoMaximoAnexo?.codigo),
		// 	true
		// );

		// ex: (Borges de Medeiros, 2255)
		// const regimeUrbanisticoProprio = ['25', '39'];
		// const regimeUrbanisticoProprioLeiEspecifica = ['41'];
		// const regimeUrbanisticoProprioSMGP = ['23'];
		// const regimeUrbanisticoProprio = planoDiretor.dadosSubunidades.reduce(
		// 	(acc, s) =>
		// 		acc &&
		// 		[
		// 			...regimeUrbanisticoProprio,
		// 			...regimeUrbanisticoProprioLeiEspecifica,
		// 			...regimeUrbanisticoProprioSMGP
		// 		].includes(s?.indiceAproveitamentoMaximoAnexo?.codigo),
		// 	true
		// );
		/* -------------------------------------------------------------------------- */

		const output = {
			soloCriadoAdensavel: codigo01ou03,
			soloCriadoNaoAdensavel: codigo01ou03,
			potencialConstrutivoTpcRealizada: codigo01ou03,
			potencialConstrutivoTpcLeilaoSmf: codigo01ou03,
			potencialConstrutivoImovelInventariado: codigo01ou03
		};

		planoDiretor.dadosSubunidades
			.map(s => `potencialConstrutivoTpcLocal_${s.subunidade.subunidade}`)
			.forEach(k => (output[k] = !possuiAreaAtingida));

		// se soloCriado Adensável e estoqueQtr.estoquePublicado
		if (estoqueQtr?.estoquePublicado === 0) {
			output.soloCriadoAdensavel = true;
		}

		debugLog('------------------------------------\n');
		planoDiretor.dadosSubunidades.forEach(s => {
			debugLog(` ---> subunidade ${s.subunidade.subunidade}:\n`, s.indiceAproveitamentoMaximoAnexo);
		});
		debugLog('readOnlyAreas', output);

		return output;
	}, [estoqueQtr, planoDiretor.dadosSubunidades, possuiAreaAtingida]);

	useEffect(() => {
		if (readOnlyAreas) {
			const validKeys = Object.keys(readOnlyAreas);
			debugLog('validKeys:', validKeys);
			const invalidKeys = Object.keys(planilha).filter(
				key => !validKeys.includes(key) && !key.startsWith('total') && !key.toLowerCase().includes('areaatingida')
			);
			debugLog('invalidKeys:', invalidKeys);
			const removedKeys = validKeys.filter(key => readOnlyAreas[key]).concat(invalidKeys);

			if (size(removedKeys) > 0) {
				debugLog('removedKeys:', removedKeys);
				const novaPlanilha = omit(planilha, removedKeys);
				updatePlanilha(novaPlanilha, true);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [readOnlyAreas]);

	useEffect(() => {
		if (totalIaPermitido) {
			updatePlanilha(planilha, primeiraVez.current);
			primeiraVez.current = false;
		}
	}, [planilha, totalIaPermitido, updatePlanilha]);

	const errors = Object.assign(
		{},
		errorsProps.reduce((acc, errorAux) => ({ ...acc, ...{ [errorAux.campo]: [errorAux.message] } }), {}) || {},
		localErrors || {}
	);

	return (
		<>
			<div className={`${readOnly ? ' read-only' : ''}`}>
				{estoqueQtr && (
					<div
						style={{
							margin: '1em 0',
							padding: '1em',
							background: '#f8f9fa',
							borderRadius: '8px',
							display: 'flex',
							alignItems: 'center',
							gap: '24px'
						}}
					>
						<dl style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '24px' }}>
							<div style={{ display: 'flex', alignItems: 'center' }}>
								<dt style={{ fontWeight: 'bold' }}>UEU Máximo:</dt>
								<dd style={{ margin: '0 8px 0 8px' }}>{estoqueQtr.ueuMaximo} m²</dd>
							</div>
							<div style={{ display: 'flex', alignItems: 'center' }}>
								<dt style={{ fontWeight: 'bold' }}>Estoque Publicado:</dt>
								<dd style={{ margin: '0 8px 0 8px' }}>{estoqueQtr.estoquePublicado} m²</dd>
							</div>
							<div style={{ display: 'flex', alignItems: 'center' }}>
								<dt style={{ fontWeight: 'bold' }}>Estoque Calculado:</dt>
								<dd style={{ marginLeft: 8 }}>{estoqueQtr.estoqueCalculado} m²</dd>
							</div>
						</dl>
					</div>
				)}
				{metadata
					.reduce((acc, area) => {
						if (area.property === 'potencialConstrutivoTpcLocal') {
							return acc.concat(
								(planoDiretor?.dadosSubunidades || []).map(s => ({
									...area,
									label: `${area.label} (subunidade ${get(s, 'subunidade.subunidade')})`,
									property: `${area.property}_${get(s, 'subunidade.subunidade')}`,
									iaSubunidade: parseFloat(get(s, 'iaRegimeUrbanisticoAnexo.codigo') || 1.0),
									iaReadonly: true
								}))
							);
						}
						return acc.concat(area);
					}, [])
					.filter(({ property }) =>
						property !== 'potencialConstrutivoImovelInventariado' ? true : possuiImovelInventariado
					)
					.map(({ label, observacao, property, decimals }, index) => {
						let areas = [NOVA_AREA_UDRI];
						const auxValue = planilha[property] || { areas };
						const auxValueAnterior = planilhaAnterior[property];
						let lockedMessage = readOnlyAreas?.[property]
							? property.startsWith('soloCriadoAdensavel')
								? 'Para este quarteirão não há estoque disponível, portanto não é possível adquirir solo criado adensável'
								: property.startsWith('potencialConstrutivoTpcLocal')
								? 'Índice não calculado - Não há área atingida para análise'
								: 'Índice não permitido por restrição do Plano Diretor Anexo 6 (códigos 01 e 03)'
							: null;
						return (
							<PlanilhaIndicesConstrutivosLinha
								key={index}
								label={label}
								observacao={observacao}
								name={property}
								value={auxValue}
								valueAnterior={auxValueAnterior}
								decimals={decimals}
								onChangeHandler={handleChange}
								readOnly={readOnly}
								locked={readOnlyAreas?.[property]}
								lockedMessage={lockedMessage}
								errors={errors?.[property]}
								usuarioInterno={usuarioInterno}
							/>
						);
					})}
			</div>
			{size(errors?.planilha) > 0 && <ErrorMessages errorList={errors.planilha} />}
			<div className={`planilha-indices-construtivos-qr-modal ${isFocoModeHistory ? ' modal-wrapper' : ''}`}>
				<div
					className={`planilha-indices-construtivos-qr table-sticky-container ${isFocoModeHistory ? 'modo-foco' : ''}`}
				>
					{isFocoModeHistory ? (
						<button
							type="button"
							className={`btn btn-sm btn-tabela-areas btn-modo-foco ${
								readOnly ? 'btn-outline-primary2' : 'btn-outline-primary'
							}${isFocoModeHistory ? ' btn-modo-foco-historico btn-modo-foco-inside' : ''}`}
							onClick={() => sairModoFoco(isFocoModeHistory)}
						>
							Sair da visualização do histórico &nbsp;
							<i className="fa fa-history" />
						</button>
					) : usuarioInterno && hasChanges ? (
						<button
							type="button"
							className={`btn btn-sm btn-tabela-areas btn-modo-foco btn-modo-foco-historico  ${
								readOnly ? 'btn-outline-primary2' : 'btn-outline-primary'
							}`}
							onClick={() => entrarModoFoco(true)}
						>
							Visualizar histórico do quadro resumo &nbsp;
							<Tippy placement="top" content={'O requerente efetuou alterações na tabela na última complementação'}>
								<i className="fa fa-exclamation-triangle" />
							</Tippy>
						</button>
					) : null}
					<ResumoIndicesConstrutivosAP
						planilha={readOnly && isFocoModeHistory && planilhaAnterior ? planilhaAnterior : planilha}
						planoDiretor={planoDiretor}
						possuiImovelInventariado={possuiImovelInventariado}
						usuarioInterno={usuarioInterno}
						isFocoModeHistory={isFocoModeHistory}
					/>
				</div>
			</div>
			<ShowDebug
				data={{
					planilha,
					planilhaAnterior,
					planoDiretor,
					possuiAreaAtingida,
					possuiImovelInventariado,
					areaAtingidaMatricula,
					areaAtingidaMenorPoligonal,
					totalIaPermitido,
					totalIaMaxTotalPermitido
				}}
				console
			/>
		</>
	);
}
PlanilhaIndicesConstrutivos.displayName = 'PlanilhaIndicesConstrutivos';
PlanilhaIndicesConstrutivos.propTypes = {
	planilha: PropTypes.object,
	planilhaAnterior: PropTypes.object,
	planoDiretor: PropTypes.object,
	possuiAreaAtingida: PropTypes.bool,
	possuiImovelInventariado: PropTypes.bool,
	areaAtingidaMatricula: PropTypes.number,
	areaAtingidaMenorPoligonal: PropTypes.number,
	totalIaPermitido: PropTypes.number,
	totalIaMaxTotalPermitido: PropTypes.number,
	readOnly: PropTypes.bool,
	readOnlyVersao: PropTypes.any,
	errors: PropTypes.any,
	onChangeHandler: PropTypes.func,
	propertyName: PropTypes.string,
	usuarioInterno: PropTypes.bool
};

export default PlanilhaIndicesConstrutivos;

function obtainTotalAreaCalculada(planilha) {
	const novoValorAdensavel =
		(planilha?.soloCriadoAdensavel?.subtotalAreaCalculada || 0) +
		Object.keys(planilha)
			.filter(key => key.startsWith('potencialConstrutivoTpcLocal'))
			.reduce((acc, key) => {
				const area = planilha[key];
				return acc + (area.subtotalAreaCalculada || 0);
			}, 0) +
		(planilha?.potencialConstrutivoTpcRealizada?.subtotalAreaCalculada || 0) +
		(planilha?.potencialConstrutivoTpcLeilaoSmf?.subtotalAreaCalculada || 0) +
		(planilha?.potencialConstrutivoImovelInventariado?.subtotalAreaCalculada || 0);

	const metadeValorAdensavel = parseFloat(divideArredonda(novoValorAdensavel, 2, { decimais: 4 }));
	const novoValorNaoAdensavel = parseFloat(
		soma(metadeValorAdensavel, planilha?.soloCriadoNaoAdensavel?.subtotalAreaCalculada || 0, { decimais: 4 })
	);

	const output = {
		totalAdensavel: parseFloat(arredonda(novoValorAdensavel, { decimais: 4 })), // getValueCutDigits(novoValorAdensavel, 4),
		totalNaoAdensavel: parseFloat(arredonda(novoValorNaoAdensavel, { decimais: 4 })) // getValueCutDigits(novoValorNaoAdensavel, 4)
	};
	return output;
}

export function validaPlanilhaIndicesConstrutivos(planilha, dadosSubunidades) {
	let errors = {};
	let warnings = {};
	metadata.forEach(m => {
		const property = m.property;
		if (((planilha[property] || {}).areas || []).find(a => !a.informada && size(trim(a.obs)) > 0)) {
			errors[property] = ['Não é possível informar observações em linhas sem área informada'];
		}
	});

	const fnTotalArea = prop => fnTotalAreaPlanilha(planilha, prop);

	const naInformado = fnTotalArea('soloCriadoNaoAdensavel');
	const adInformado = fnTotalArea('soloCriadoAdensavel');

	if (adInformado > planilha.estoquePublicado) {
		errors.soloCriadoAdensavel = ['O solo criado adensável informado ultrapassa o estoque disponível do quarteirão'];
	}

	const totalAreas = naInformado + adInformado;

	const totalTpc = Object.keys(planilha)
		.filter(key => key.startsWith('potencialConstrutivoTpc'))
		.reduce((acc, prop) => acc + fnTotalArea(prop), 0);

	const total = totalAreas + totalTpc;

	if (total === 0) {
		errors.planilha = ['Pelo menos uma área deve ser informada na planilha'];
	}
	if (errors.soloCriadoAdensavel) {
		errors.planilha = errors.soloCriadoAdensavel;
	}

	Object.keys(planilha)
		.filter(key => key.startsWith('potencialConstrutivoTpcLocal'))
		.forEach(prop => {
			const [, subunidade] = prop.split('_');
			const sub = dadosSubunidades.find(s => s.subunidade?.subunidade === parseInt(subunidade));
			const ia = parseFloat(sub?.iaRegimeUrbanisticoAnexo?.codigo || 1.0);
			const valorConsiderado = planilha?.areaAtingidaMenorPoligonal || planilha?.areaAtingidaMatricula || 0;
			const valorMaximo = valorConsiderado * ia;
			debugLog('Para cálculo do valor máximo de TPC Local:');
			debugLog(
				`valorConsiderado (${
					planilha?.areaAtingidaMenorPoligonal ? 'areaAtingidaMenorPoligonal' : 'areaAtingidaMatricula'
				}): ${valorConsiderado}`
			);
			debugLog(`valorCo: ${ia}`);
			debugLog(`valorMaximo: ${valorMaximo}`);
			if (fnTotalArea(prop) > valorMaximo) {
				debugLog(`valor informado (${fnTotalArea(prop)}) é maior que o permitido`);
				warnings[prop] = [
					`O valor informado é maior que o permitido (área atingida * Índice de Aproveitamento da subunidade)${
						isDebug ? `(debug - valor máximo: ${valorMaximo})` : ''
					}`
				];
			}
			debugLog('------------------------------------------');
		});

	return [errors, warnings];
}

export function fnTotalAreaPlanilha(planilha, prop) {
	return (planilha?.[prop]?.areas || []).reduce((acc, a) => acc + a.informada, 0);
}
